/**
 * Step 2 — Source Materials input.
 *
 * Three input modes that all flow into a single freeform synthesize call:
 *   - File drop zone — PDF, PNG, JPG, WebP, plain text.  Each file is
 *     uploaded immediately to the orphan-attachment endpoint
 *     (POST /api/v1/generation/attachments) which extracts text/summary
 *     and returns a stable attachment_id.
 *   - URL list textarea — pasted competitor URLs / landing page refs that
 *     get appended to the synthesis prompt.
 *   - Pasted reference content — long-form text excerpts from brand guides,
 *     scripts, etc., also appended to the prompt.
 *
 * On submit, ``synthesizeBrief`` is called with both the prompt body
 * (URLs + paste) AND ``attachment_ids[]`` from the uploaded files.  The
 * new brief receives copies of those attachments via the existing
 * synthesize copy logic, and the orphan staging entries are cleared.
 */

import { useCallback, useState } from 'react';
import { FileText, Image as ImageIcon, Loader2, Paperclip, X } from 'lucide-react';
import { generationApi, type GenerationBrief } from '@/services/api';

interface StagedAttachment {
  attachment_id: string;
  filename: string;
  content_type: string;
  file_size_bytes: number;
  extraction_status?: string;
  extracted_summary?: string;
  uploading?: boolean;
  error?: string;
}

interface Props {
  clientUid: string;
  personaId?: string;
  organizationId?: string;
  onSynthesized: (brief: GenerationBrief) => void;
}

const ACCEPTED_TYPES =
  'application/pdf,image/png,image/jpeg,image/webp,text/plain,text/markdown';

export function SourceMaterialsInput({
  clientUid,
  personaId: _personaId,
  organizationId,
  onSynthesized,
}: Props) {
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [pastedContent, setPastedContent] = useState('');
  const [referenceUrls, setReferenceUrls] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!clientUid) {
        setError('Pick a client first.');
        return;
      }
      const fileArr = Array.from(files);
      // Optimistic placeholders so the user sees the upload start.
      const placeholders: StagedAttachment[] = fileArr.map((f) => ({
        attachment_id: `pending_${Math.random().toString(36).slice(2, 10)}`,
        filename: f.name,
        content_type: f.type || 'application/octet-stream',
        file_size_bytes: f.size,
        uploading: true,
      }));
      setStaged((prev) => [...prev, ...placeholders]);

      for (let i = 0; i < fileArr.length; i++) {
        const file = fileArr[i];
        const placeholderId = placeholders[i].attachment_id;
        try {
          const att = await generationApi.uploadOrphanAttachment(file, {
            client_uid: clientUid,
            organization_id: organizationId,
            category: 'general',
          });
          setStaged((prev) =>
            prev.map((s) =>
              s.attachment_id === placeholderId
                ? {
                    attachment_id: att.attachment_id,
                    filename: att.filename,
                    content_type: att.content_type,
                    file_size_bytes: att.file_size_bytes,
                    extraction_status: att.extraction_status,
                    extracted_summary: att.extracted_summary,
                    uploading: false,
                  }
                : s,
            ),
          );
        } catch (err) {
          setStaged((prev) =>
            prev.map((s) =>
              s.attachment_id === placeholderId
                ? { ...s, uploading: false, error: (err as Error).message }
                : s,
            ),
          );
        }
      }
    },
    [clientUid, organizationId],
  );

  const removeStaged = useCallback(async (attachmentId: string) => {
    setStaged((prev) => prev.filter((s) => s.attachment_id !== attachmentId));
    if (!attachmentId.startsWith('pending_')) {
      try {
        await generationApi.deleteOrphanAttachment(attachmentId);
      } catch {
        // best-effort — server may have already cleaned up
      }
    }
  }, []);

  const generate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const urls = referenceUrls
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const parts: string[] = [];
      if (pastedContent.trim()) {
        parts.push('Pasted reference material:');
        parts.push(pastedContent.trim());
      }
      if (urls.length > 0) {
        parts.push('');
        parts.push('Reference URLs to consider:');
        parts.push(...urls.map((u) => `- ${u}`));
      }
      const readyAttachments = staged.filter(
        (s) => !s.uploading && !s.error && !s.attachment_id.startsWith('pending_'),
      );
      if (parts.length === 0 && readyAttachments.length === 0) {
        setError(
          'Add at least one file, URL, or pasted reference material to continue.',
        );
        setSubmitting(false);
        return;
      }
      if (readyAttachments.length > 0) {
        if (parts.length === 0) {
          parts.push('Synthesize a creative brief grounded in the attached materials.');
        }
        parts.push('');
        parts.push(
          `Attached materials (${readyAttachments.length}): ${readyAttachments
            .map((s) => s.filename)
            .join(', ')}`,
        );
      }
      const brief = await generationApi.synthesizeBrief({
        client_uid: clientUid,
        prompt: parts.join('\n'),
        attachment_ids: readyAttachments.map((s) => s.attachment_id),
      });
      onSynthesized(brief);
    } catch (err) {
      setError((err as Error).message || 'Synthesis failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <FileDropZone
        dragOver={dragOver}
        disabled={!clientUid || submitting}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length > 0) {
            void handleFiles(e.dataTransfer.files);
          }
        }}
        onPick={(files) => void handleFiles(files)}
      />

      {staged.length > 0 && (
        <ul className="space-y-1.5">
          {staged.map((s) => (
            <StagedRow
              key={s.attachment_id}
              entry={s}
              onRemove={() => removeStaged(s.attachment_id)}
            />
          ))}
        </ul>
      )}

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Reference URLs (one per line)
        </label>
        <textarea
          value={referenceUrls}
          onChange={(e) => setReferenceUrls(e.target.value)}
          placeholder="https://example.com/landing-page&#10;https://example.com/competitor-ad"
          rows={3}
          disabled={submitting}
          className="mt-1 w-full resize-none text-[12px] font-mono text-slate-700 placeholder:text-slate-400 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-300/40 focus:border-amber-400/60 disabled:bg-slate-50"
        />
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Pasted reference content <span className="text-slate-400 normal-case">(optional)</span>
        </label>
        <textarea
          value={pastedContent}
          onChange={(e) => setPastedContent(e.target.value)}
          placeholder="Paste brand guide excerpts, ad scripts, competitor teardown notes…"
          rows={5}
          disabled={submitting}
          className="mt-1 w-full resize-none text-sm text-slate-800 placeholder:text-slate-400 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-300/40 focus:border-amber-400/60 disabled:bg-slate-50"
        />
      </div>

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-slate-500">
          {staged.length === 0
            ? 'Drop a file, paste URLs, or write reference notes.'
            : `${staged.filter((s) => !s.uploading && !s.error).length} of ${staged.length} ready`}
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={submitting || !clientUid || staged.some((s) => s.uploading)}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50"
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
          Generate Draft from Materials →
        </button>
      </div>
    </div>
  );
}

function FileDropZone({
  dragOver,
  disabled,
  onDragOver,
  onDragLeave,
  onDrop,
  onPick,
}: {
  dragOver: boolean;
  disabled: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onPick: (files: FileList) => void;
}) {
  return (
    <label
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`block rounded-xl border-2 border-dashed px-6 py-8 text-center cursor-pointer transition ${
        dragOver
          ? 'border-amber-400 bg-amber-50'
          : 'border-slate-300 bg-slate-50 hover:border-amber-300 hover:bg-amber-50/40'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <Paperclip size={20} className="text-amber-500 mx-auto mb-2" />
      <p className="text-sm font-semibold text-slate-700">
        Drop files or click to upload
      </p>
      <p className="text-[11px] text-slate-500 mt-1">
        PDF · PNG · JPG · WebP · TXT · up to 25 MB each
      </p>
      <input
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        disabled={disabled}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            onPick(e.target.files);
            // Reset value so the same file can be re-uploaded if removed.
            e.target.value = '';
          }
        }}
        className="hidden"
      />
    </label>
  );
}

function StagedRow({
  entry,
  onRemove,
}: {
  entry: StagedAttachment;
  onRemove: () => void;
}) {
  const Icon = entry.content_type.startsWith('image/') ? ImageIcon : FileText;
  return (
    <li className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="w-8 h-8 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-slate-800 truncate">
          {entry.filename}
        </div>
        <div className="text-[10px] text-slate-500">
          {formatSize(entry.file_size_bytes)}
          {entry.uploading && ' · uploading…'}
          {!entry.uploading && entry.extraction_status === 'completed' && ' · ready'}
          {!entry.uploading && entry.extraction_status === 'processing' && ' · extracting…'}
          {!entry.uploading && entry.extraction_status === 'failed' && ' · extraction failed'}
        </div>
        {entry.error && (
          <div className="text-[10px] text-rose-600 mt-0.5">{entry.error}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-400 hover:text-rose-500 transition"
        title="Remove"
      >
        <X size={14} />
      </button>
    </li>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
